/**
 * Unified Error Handler - Phase 3 Enhanced
 * 
 * Primary error handling system for the application.
 * Consolidates all error handling patterns into a single, robust system.
 * 
 * @version 3.0.0 - Phase 3: Consolidated and Enhanced
 */

import { ERROR_CODES, ERROR_MESSAGES } from '../constants/errorCodes';

// Error monitoring and analytics
let errorHistory = [];
const MAX_ERROR_HISTORY = 100;

/**
 * Phase 3: Error Analytics and Monitoring
 */
export const ErrorMonitor = {
  /**
   * Track error occurrence for monitoring
   */
  trackError(error, context = {}) {
    const errorRecord = {
      timestamp: new Date().toISOString(),
      message: error.message || error.toString(),
      code: error.code || 'UNKNOWN',
      context,
      userAgent: navigator.userAgent,
      url: window.location.href,
      stack: error.stack
    };
    
    errorHistory.push(errorRecord);
    
    // Keep only recent errors
    if (errorHistory.length > MAX_ERROR_HISTORY) {
      errorHistory = errorHistory.slice(-MAX_ERROR_HISTORY);
    }
    
    return errorRecord;
  },
  
  /**
   * Get error statistics
   */
  getStats() {
    const now = Date.now();
    const last24Hours = errorHistory.filter(e => 
      now - new Date(e.timestamp).getTime() < 24 * 60 * 60 * 1000
    );
    
    const errorCounts = {};
    last24Hours.forEach(error => {
      errorCounts[error.code] = (errorCounts[error.code] || 0) + 1;
    });
    
    return {
      total: errorHistory.length,
      last24Hours: last24Hours.length,
      topErrors: Object.entries(errorCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5),
      history: errorHistory
    };
  },
  
  /**
   * Clear error history
   */
  clearHistory() {
    errorHistory = [];
  }
};

/**
 * Phase 3: Enhanced Error Classification
 */
export const classifyError = (error) => {
  const message = error.message || error.toString();
  const code = error.code || error.response?.data?.code;
  
  // Check for specific error codes first
  if (code && ERROR_MESSAGES[code]) {
    return {
      code,
      type: 'known_error',
      title: 'Error',
      message: ERROR_MESSAGES[code],
      severity: 'error',
      actionable: true
    };
  }
    
  // Pattern-based classification
  const patterns = [
    {
      pattern: /network|fetch|connection/i,
      type: 'network',
      title: 'Connection Error',
      message: 'Unable to connect to server. Please check your internet connection.',
      severity: 'error',
      actionable: true
    },
    {
      pattern: /401|unauthorized|token.*expired/i,
      type: 'authentication',
      title: 'Session Expired',
      message: 'Your session has expired. Please log in again.',
      severity: 'warning',
      actionable: true
    },
    {
      pattern: /403|forbidden|permission/i,
      type: 'authorization',
      title: 'Access Denied',
      message: 'You do not have permission to perform this action.',
      severity: 'error',
      actionable: false
    },
    {
      pattern: /404|not found/i,
      type: 'not_found',
      title: 'Not Found',
      message: 'The requested resource was not found.',
      severity: 'error',
      actionable: false
    },
    {
      pattern: /500|internal.*server/i,
      type: 'server_error',
      title: 'Server Error',
      message: 'An internal server error occurred. Please try again later.',
      severity: 'error',
      actionable: true
    },
    {
      pattern: /file.*size|too large|exceeds.*limit/i,
      type: 'file_size',
      title: 'File Too Large',
      message: 'The file you uploaded is too large. Please upload a smaller file.',
      severity: 'warning',
      actionable: true
    },
    {
      pattern: /invalid.*format|unsupported.*type/i,
      type: 'format_error',
      title: 'Invalid Format',
      message: 'The file format is not supported. Please upload a different file.',
      severity: 'warning',
      actionable: true
    }
  ];
  
  for (const { pattern, type, title, message: msg, severity, actionable } of patterns) {
    if (pattern.test(message)) {
      return {
        code: code || 'PATTERN_MATCH',
        type,
        title,
        message: msg,
        severity,
        actionable,
        originalMessage: message
      };
    }
  }
  
  // Default classification
  return {
    code: code || 'UNKNOWN',
    type: 'general',
    title: 'Error',
    message: message || 'An unexpected error occurred',
    severity: 'error',
    actionable: true,
    originalMessage: message
  };
};

/**
 * Phase 3: Enhanced API Error Handler
 * Main error handling function - updated to handle Phase 1 backend format
 */
export const handleApiError = (error, context = {}) => {
  // Track error for monitoring
  ErrorMonitor.trackError(error, context);

  // Handle network failures and timeouts (no error.response)
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return {
        message: 'Request timed out. Please try again.',
        code: ERROR_CODES.SERVICE_TIMEOUT,
        details: undefined
      };
    }

    if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
      return {
        message: ERROR_MESSAGES[ERROR_CODES.NETWORK_ERROR],
        code: ERROR_CODES.NETWORK_ERROR,
        details: undefined
      };
    }

    return {
      message: 'Unable to reach server. Please check your connection.',
      code: ERROR_CODES.NETWORK_ERROR,
      details: undefined
    };
  }

  // Extract error information based on backend response format
  const { status, data } = error.response;
  let errorInfo = null;

  // Phase 1 & 2: Handle new backend format {error, details, code}
  if (data?.error && data?.code) {
    // New standardized format from Phase 1
    // Always use the specific backend error message, don't override with frontend mappings
    errorInfo = {
      code: data.code,
      message: data.error, // This preserves "Invalid credentials" from backend
      details: data.details,
      httpStatus: status
    };
  } else if (data?.message) {
    // Fallback for other response formats
    errorInfo = {
      code: data.code || 'API_ERROR',
      message: data.message,
      details: data.details || data.error,
      httpStatus: status
    };
  }

  // If no structured error info, use HTTP status
  if (!errorInfo) {
    switch (status) {
      case 400:
        errorInfo = {
          code: ERROR_CODES.VALIDATION_ERROR,
          message: ERROR_MESSAGES[ERROR_CODES.VALIDATION_ERROR],
          details: data?.details,
          httpStatus: status
        };
        break;
      case 401:
        // For 401 errors, prefer specific backend message over generic unauthorized message
        const authErrorMessage = data?.error || data?.message;
        errorInfo = {
          code: ERROR_CODES.UNAUTHORIZED,
          message: authErrorMessage || ERROR_MESSAGES[ERROR_CODES.UNAUTHORIZED],
          details: data?.details,
          httpStatus: status
        };
        break;
      case 403:
        errorInfo = {
          code: ERROR_CODES.ACCESS_DENIED,
          message: ERROR_MESSAGES[ERROR_CODES.ACCESS_DENIED],
          details: undefined,
          httpStatus: status
        };
        break;
      case 404:
        const backendErrorCode = data?.code || data?.errorCode;
        
        if (backendErrorCode && ERROR_CODES[backendErrorCode]) {
          errorInfo = {
            code: backendErrorCode,
            message: data?.error || data?.message || ERROR_MESSAGES[backendErrorCode],
            details: data?.details,
            httpStatus: status
          };
        } else {
          errorInfo = {
            code: ERROR_CODES.CLAIM_NOT_FOUND,
            message: ERROR_MESSAGES[ERROR_CODES.CLAIM_NOT_FOUND],
            details: undefined,
            httpStatus: status
          };
        }
        break;
      case 413:
        errorInfo = {
          code: ERROR_CODES.FILE_TOO_LARGE,
          message: ERROR_MESSAGES[ERROR_CODES.FILE_TOO_LARGE],
          details: data?.details,
          httpStatus: status
        };
        break;
      case 415:
        errorInfo = {
          code: ERROR_CODES.INVALID_FILE_TYPE,
          message: ERROR_MESSAGES[ERROR_CODES.INVALID_FILE_TYPE],
          details: data?.details,
          httpStatus: status
        };
        break;
      case 422:
        errorInfo = {
          code: ERROR_CODES.VALIDATION_ERROR,
          message: ERROR_MESSAGES[ERROR_CODES.VALIDATION_ERROR],
          details: data?.details,
          httpStatus: status
        };
        break;
      case 429:
        errorInfo = {
          code: ERROR_CODES.SERVICE_TIMEOUT,
          message: 'Too many requests. Please wait a moment and try again.',
          details: data?.details,
          httpStatus: status
        };
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        errorInfo = {
          code: ERROR_CODES.INTERNAL_SERVER_ERROR,
          message: ERROR_MESSAGES[ERROR_CODES.INTERNAL_SERVER_ERROR],
          details: data?.details,
          httpStatus: status
        };
        break;
      default:
        // Try to determine specific not found type based on message
        const backendMessage = data?.message || data?.error;
        const backendCode = data?.code;
        const backendDetails = data?.details;
        if (status === 404) {
          if (backendMessage?.toLowerCase().includes('claim')) {
            errorInfo = {
              message: ERROR_MESSAGES[ERROR_CODES.CLAIM_NOT_FOUND],
              code: ERROR_CODES.CLAIM_NOT_FOUND,
              details: backendDetails,
              httpStatus: status
            };
          } else if (backendMessage?.toLowerCase().includes('document')) {
            errorInfo = {
              message: ERROR_MESSAGES[ERROR_CODES.DOCUMENT_NOT_FOUND],
              code: ERROR_CODES.DOCUMENT_NOT_FOUND,
              details: backendDetails,
              httpStatus: status
            };
          } else if (backendMessage?.toLowerCase().includes('user')) {
            errorInfo = {
              message: ERROR_MESSAGES[ERROR_CODES.USER_NOT_FOUND],
              code: ERROR_CODES.USER_NOT_FOUND,
              details: backendDetails,
              httpStatus: status
            };
          } else {
            errorInfo = {
              message: backendMessage || 'The requested resource was not found.',
              code: backendCode || ERROR_CODES.CLAIM_NOT_FOUND,
              details: backendDetails,
              httpStatus: status
            };
          }
        } else {
          errorInfo = {
            message: backendMessage || ERROR_MESSAGES[ERROR_CODES.INTERNAL_SERVER_ERROR],
            code: backendCode || ERROR_CODES.INTERNAL_SERVER_ERROR,
            details: backendDetails,
            httpStatus: status
          };
        }
        break;
    }
  }

  // Enhance with user-friendly messages
  if (ERROR_MESSAGES[errorInfo.code]) {
    errorInfo.userMessage = ERROR_MESSAGES[errorInfo.code];
  }

  return errorInfo;
};

/**
 * Extract validation errors from backend response
 * Handles the new {error, details, code} format from Phase 1 backend
 * @param {Object} errorData - Error data from backend
 * @returns {Object} - Field-specific validation errors
 */
export const extractValidationErrors = (errorData) => {
  if (!errorData?.details) {
    return {};
  }

  // If details is already an object with field errors, return it
  if (typeof errorData.details === 'object' && !Array.isArray(errorData.details)) {
    return errorData.details;
  }

  // If details is a string, try to parse validation info from it
  if (typeof errorData.details === 'string') {
    const details = errorData.details;
    
    // Handle common validation patterns
    if (details.includes('Ghana Card number')) {
      return { ghanaCardNumber: details };
    }
    if (details.includes('Image file')) {
      return { image: details };
    }
    if (details.includes('required')) {
      // Try to extract field name from "X is required" messages
      const fieldMatch = details.match(/(\w+)\s+is\s+required/i);
      if (fieldMatch) {
        return { [fieldMatch[1]]: details };
      }
    }
    
    // Return as general validation error
    return { general: details };
  }

  // If details is an array (e.g., from Joi validation), convert to object
  if (Array.isArray(errorData.details)) {
    const fieldErrors = {};
    errorData.details.forEach(detail => {
      if (detail.path && detail.message) {
        fieldErrors[detail.path.join('.')] = detail.message;
      }
    });
    return fieldErrors;
  }

  return {};
};

/**
 * Check if error is a network/connection error
 * @param {Error} error - The error object
 * @returns {boolean} - True if it's a network error
 */
export const isNetworkError = (error) => {
  return !error.response || 
         error.code === 'ERR_NETWORK' || 
         error.code === 'ECONNABORTED' ||
         error.message?.includes('Network Error') ||
         error.message?.includes('timeout');
};

/**
 * Check if error indicates session expiry
 * @param {Error} error - The error object  
 * @returns {boolean} - True if session expired
 */
export const isSessionExpired = (error) => {
  const { status, data } = error.response || {};
  return status === 401 || 
         data?.code === ERROR_CODES.TOKEN_EXPIRED ||
         data?.code === ERROR_CODES.UNAUTHORIZED;
};
